
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useBuyer } from "@/contexts/BuyerContext";
import { useAuth } from "@/contexts/AuthContext";
import { EmailUpdateDialog } from "@/components/auth/EmailUpdateDialog";
import { AddressAutocomplete } from "@/components/properties/AddressAutocomplete";
import { SMSPreferencesSection } from "@/components/user/SMSPreferencesSection";
import { toast } from "@/hooks/use-toast";

const buyerPersonalInfoSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  companyName: z.string().min(1, "Company name is required"),
  phoneNumber: z.string().min(1, "Phone number is required"),
  dealsAnnually: z.number().min(0, "Deals annually must be a positive number"),
  address: z.string().min(1, "Street address is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zipCode: z.string().min(1, "ZIP code is required"),
});

type BuyerPersonalInfoForm = z.infer<typeof buyerPersonalInfoSchema>;

const BuyerPersonalInfoForm = () => {
  const { buyerProfile, updateBuyerProfile, isLoading } = useBuyer();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  const form = useForm<BuyerPersonalInfoForm>({
    resolver: zodResolver(buyerPersonalInfoSchema),
    defaultValues: {
      fullName: "",
      companyName: "",
      phoneNumber: "",
      dealsAnnually: 0,
      address: "",
      city: "",
      state: "",
      zipCode: "",
    },
  });

  // Update form values when buyer profile loads
  useEffect(() => {
    if (buyerProfile) {
      form.reset({
        fullName: buyerProfile.full_name || "",
        companyName: buyerProfile.company_name || "",
        phoneNumber: buyerProfile.phone_number || "",
        dealsAnnually: buyerProfile.deals_annually || 0,
        address: buyerProfile.address || "",
        city: buyerProfile.city || "",
        state: buyerProfile.state || "",
        zipCode: buyerProfile.zip_code || "",
      });
    }
  }, [buyerProfile, form]);

  const onSubmit = async (data: BuyerPersonalInfoForm) => {
    try {
      await updateBuyerProfile({
        full_name: data.fullName,
        company_name: data.companyName || null,
        phone_number: data.phoneNumber,
        deals_annually: data.dealsAnnually,
        address: data.address || null,
        city: data.city || null,
        state: data.state || null,
        zip_code: data.zipCode || null,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const handleCancel = () => {
    if (buyerProfile) {
      form.reset({
        fullName: buyerProfile.full_name || "",
        companyName: buyerProfile.company_name || "",
        phoneNumber: buyerProfile.phone_number || "",
        dealsAnnually: buyerProfile.deals_annually || 0,
        address: buyerProfile.address || "",
        city: buyerProfile.city || "",
        state: buyerProfile.state || "",
        zipCode: buyerProfile.zip_code || "",
      });
    }
    setIsEditing(false);
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Personal Information</CardTitle>
          {!isEditing && (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          disabled={!isEditing}
                          className={!isEditing ? "bg-muted" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          disabled={!isEditing}
                          className={!isEditing ? "bg-muted" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Email Field with Change Button */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-muted cursor-not-allowed"
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setShowEmailDialog(true)}
                  >
                    Change Email
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  This is your login email address
                </p>
              </div>

              {/* Address Field with Autocomplete */}
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street Address</FormLabel>
                    <FormControl>
                      <AddressAutocomplete
                        initialAddress={field.value || ""}
                        onAddressSelect={(addressData) => {
                          form.setValue("address", addressData.address);
                          form.setValue("city", addressData.city);
                          form.setValue("state", addressData.state);
                          form.setValue("zipCode", addressData.zipCode);
                        }}
                        onInputChange={field.onChange}
                        className={!isEditing ? "bg-muted" : ""}
                        showLabel={false}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          disabled={!isEditing}
                          className={!isEditing ? "bg-muted" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          disabled={!isEditing}
                          className={!isEditing ? "bg-muted" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="zipCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ZIP Code</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          disabled={!isEditing}
                          className={!isEditing ? "bg-muted" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <PhoneInput
                          value={field.value}
                          onChange={field.onChange}
                          disabled={!isEditing}
                          className={!isEditing ? "bg-muted" : ""}
                          showValidation={isEditing}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dealsAnnually"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deals Completed Annually</FormLabel>
                      <FormControl>
                        <Input 
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                          disabled={!isEditing}
                          className={!isEditing ? "bg-muted" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {isEditing && (
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isLoading}
                    className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
                  >
                    {isLoading ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              )}
            </form>
          </Form>
        </CardContent>

        <EmailUpdateDialog
          currentEmail={user?.email || ''}
          userType="buyer"
          open={showEmailDialog}
          onOpenChange={setShowEmailDialog}
          onSuccess={() => {
            setShowEmailDialog(false);
            toast({
              title: "Verification Email Sent",
              description: "Please check your new email address to complete the change.",
            });
          }}
        />
      </Card>

      {/* SMS Preferences Section */}
      {buyerProfile && (
        <div className="mt-6">
          <SMSPreferencesSection
            userType="buyer"
            initialSmsOptIn={buyerProfile.sms_opt_in || false}
            buyerProfileId={buyerProfile.id}
          />
        </div>
      )}
    </>
  );
};

export default BuyerPersonalInfoForm;
